// Bước "mã QR" của màn Đăng nhập (docs/KE_HOACH_DANG_NHAP_QR.md): hiện mã, hỏi máy chủ mỗi 2 giây khi tab đang hiện
// (quay lại tab thì hỏi ngay). Điện thoại quét → thay mã bằng chữ cái tài khoản; bấm "Đăng nhập" trên điện thoại →
// onSignedIn. Mã hết hạn tự đổi tối đa AUTO_RENEW lần rồi dừng — tab bỏ quên không hỏi máy chủ cả ngày.

import { useCallback, useEffect, useRef, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { AlertCircle, RefreshCw, ScanLine } from 'lucide-react'
import { Spinner } from '@/components/ui/spinner'
import { ApiError } from '@/lib/api'
import { pollQrLogin, startQrLogin, type QrLoginPoll, type QrLoginStart } from '@/lib/auth'
import { qrLoginLink } from '@/lib/qrLogin'

const AUTO_RENEW = 3

type View =
  | { kind: 'loading' }
  | { kind: 'pending' }
  | { kind: 'scanned'; initials: string }
  | { kind: 'done'; initials: string }
  | { kind: 'stale'; reason: 'expired' | 'rejected' }
  | { kind: 'error'; message: string }

export type QrSignedIn = Extract<QrLoginPoll, { status: 'approved' }>

export function QrLoginPanel({ onSignedIn }: { onSignedIn: (r: QrSignedIn) => void }) {
  const [qr, setQr] = useState<QrLoginStart | null>(null)
  const [view, setView] = useState<View>({ kind: 'loading' })
  const renewals = useRef(0)
  const signedIn = useRef(onSignedIn)
  signedIn.current = onSignedIn

  const fetchCode = useCallback(async () => {
    setView({ kind: 'loading' })
    try {
      setQr(await startQrLogin())
      setView({ kind: 'pending' })
    } catch (err) {
      setQr(null)
      setView({
        kind: 'error',
        message: err instanceof ApiError && err.code === 'rate_limited'
          ? 'Lấy mã quá nhiều lần. Đợi một phút rồi thử lại.'
          : (err as Error).message || 'Không lấy được mã',
      })
    }
  }, [])

  // Xin mã một lần khi mở (StrictMode chạy effect hai lần ở dev — không xin hai mã)
  const opened = useRef(false)
  useEffect(() => {
    if (opened.current) return
    opened.current = true
    void fetchCode()
  }, [fetchCode])

  // Một vòng hỏi cho mỗi mã; dừng khi đã vào / hết hạn / bị huỷ / lỗi
  useEffect(() => {
    if (!qr) return
    let stop = false
    let busy = false
    let timer: number | undefined
    const later = () => {
      if (!stop) timer = window.setTimeout(tick, qr.interval * 1000)
    }
    async function tick() {
      if (stop || busy) return
      if (document.hidden) return later()
      busy = true
      try {
        const r = await pollQrLogin(qr!.pollToken)
        // Đã nhận phiên (máy chủ xoá yêu cầu) thì phải vào dù vừa rời bước này
        if (r.status === 'approved') {
          stop = true
          setView((v) => ({ kind: 'done', initials: v.kind === 'scanned' ? v.initials : '' }))
          signedIn.current(r)
          return
        }
        if (stop) return
        if (r.status === 'scanned') {
          setView((v) => (v.kind === 'scanned' && v.initials === r.initials ? v : { kind: 'scanned', initials: r.initials }))
        } else if (r.status === 'rejected') {
          return setView({ kind: 'stale', reason: 'rejected' })
        } else if (r.status === 'expired') {
          if (renewals.current < AUTO_RENEW) {
            renewals.current += 1
            void fetchCode()
          } else {
            setView({ kind: 'stale', reason: 'expired' })
          }
          return
        }
      } catch (err) {
        if (stop) return
        // Điện thoại đã duyệt mà tài khoản không vào được (bị khoá, nhân viên bị gỡ…): báo đúng lý do
        if (err instanceof ApiError && err.statusCode < 500 && err.code !== 'rate_limited') {
          return setView({ kind: 'error', message: err.message })
        }
        // mạng chập chờn / máy chủ lỗi / giới hạn tần suất: hỏi lại lần sau
      } finally {
        busy = false
      }
      later()
    }
    const onVisible = () => {
      if (document.hidden || stop) return
      window.clearTimeout(timer)
      void tick()
    }
    later()
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      stop = true
      window.clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [qr, fetchCode])

  const renew = () => {
    renewals.current = 0
    void fetchCode()
  }
  const veiled = view.kind === 'stale' || view.kind === 'error'
  const who = view.kind === 'scanned' || view.kind === 'done' ? view.initials : null

  return (
    <div className="lg-qr">
      <div className="lg-qr-box" data-state={view.kind}>
        {who !== null ? (
          <span className="lg-qr-who">
            <span className="lg-av lg-av-lg" aria-hidden>{who || '✓'}</span>
          </span>
        ) : qr ? (
          <QRCodeSVG value={qrLoginLink(qr.code)} size={208} level="M" marginSize={0} className="lg-qr-code" title="Mã đăng nhập" />
        ) : null}
        {view.kind === 'loading' && <span className="lg-qr-veil"><Spinner className="size-6 text-[rgb(var(--accent))]" /></span>}
        {veiled && (
          <span className="lg-qr-veil">
            <button type="button" className="lg-qr-renew" onClick={renew}>
              <RefreshCw className="size-4" aria-hidden />
              Lấy mã mới
            </button>
          </span>
        )}
      </div>
      <QrMessage view={view} />
    </div>
  )
}

function QrMessage({ view }: { view: View }) {
  switch (view.kind) {
    case 'scanned':
      return <p className="lg-qr-msg" aria-live="polite"><strong>Đã quét.</strong> Bấm Đăng nhập trên điện thoại</p>
    case 'done':
      return <p className="lg-qr-msg" aria-live="polite"><Spinner className="size-4" /> Đang vào…</p>
    case 'stale':
      return <p className="lg-qr-msg" aria-live="polite">{view.reason === 'rejected' ? 'Đã huỷ trên điện thoại' : 'Mã đã hết hạn'}</p>
    case 'error':
      return (
        <p className="lg-error" role="alert">
          <AlertCircle className="mt-px size-4 shrink-0" aria-hidden />
          <span>{view.message}</span>
        </p>
      )
    default:
      return (
        <p className="lg-qr-msg">
          Mở ShopTrack trên điện thoại, chọn <strong className="whitespace-nowrap"><ScanLine className="lg-qr-icon" aria-hidden /> Quét mã đăng nhập</strong>
        </p>
      )
  }
}
